import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import { BreadcrumbType } from "src/components/Breadcrumb/Breadcrumbs";

export type ElementType = "headings" | "subheadings" | "properties" | "subproperties" | "lca" | "component" | "detail";

export type DrilldownState = {
    id: string;
    type: ElementType | string;
    label: string;
    color?: string;
    extraAction?: (state: DrilldownState) => void;
};

type DrilldownInitOptions = {
    goBackLink: string;
    title: string;
    onReset?: () => void;
};

export type Drilldown = {
    breadcrumbs: BreadcrumbType[];
    drilldown: (item: DrilldownState) => void;
    state: DrilldownState[];
    last: DrilldownState | undefined;
    reset: () => void;
};
export const useDrilldown = (options: DrilldownInitOptions) => {
    const navigate = useNavigate();
    const { t } = useTranslation();
    const { goBackLink, title, onReset } = options;

    const initItems: BreadcrumbType[] = [
        {
            label: t("breadcrumbs.scores_and_indicators"),
            action: () => {
                navigate(goBackLink);
            },
        },
        {
            label: title,
            action: () => {
                reset();
                onReset?.();
            },
        },
    ];
    const startingLengthPosition = initItems.length;

    const [breadcrumbs, _setBreadcrumbs] = useState<BreadcrumbType[]>(initItems);
    const [state, _setDrilldownState] = useState<DrilldownState[]>([]);

    const drilldown = (item: DrilldownState) => {
        const newBreadcrumb: BreadcrumbType = {
            label: item.label,
            action: () => {
                const newItemPos = breadcrumbs.length + 1;
                item.extraAction?.(item);
                _setDrilldownState((prev) => {
                    const newDrilldownState = prev.slice(0, newItemPos - startingLengthPosition);
                    return newDrilldownState;
                });
                _setBreadcrumbs((prev) => {
                    const newItems = prev.slice(0, newItemPos);
                    return newItems;
                });
            },
        };

        _setDrilldownState([...state, item]);
        _setBreadcrumbs([...breadcrumbs, newBreadcrumb]);
    };

    const last = state.at(-1);

    const reset = () => {
        _setBreadcrumbs(initItems);
        _setDrilldownState([]);
    };

    return { breadcrumbs, drilldown, state, last, reset };
};
